import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import ChapterHeader from "@/components/landing/ChapterHeader";
import { LIEVITATI } from "@/lib/constants";
import { useLang } from "@/lib/i18n";

export default function Lievitati() {
  const { t } = useLang();
  const [selected, setSelected] = useState(null);

  return (
    <section id="lievitati" data-testid="lievitati-section" className="mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36">
      <ChapterHeader number="04" title={t.lievitati.title} subtitle={t.lievitati.subtitle} />
      <div className="grid gap-10 md:grid-cols-3">
        {LIEVITATI.map((l, i) => {
          const tt = t.lievitati.items[l.id];
          return (
            <motion.article
              key={l.id}
              data-testid={`lievitati-card-${l.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="group"
            >
              <button
                data-testid={`lievitato-zoom-${l.id}`}
                onClick={() => setSelected({ ...l, badge: tt.badge })}
                className="relative block aspect-[4/5] w-full cursor-zoom-in overflow-hidden rounded-[2rem] border border-bordeaux/10 shadow-[0_30px_60px_-30px_rgba(171,24,43,0.3)]"
                aria-label={l.name}
              >
                <img
                  src={l.image}
                  alt={l.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 rounded-full bg-bordeaux px-4 py-1.5 font-mono-alt text-[10px] uppercase tracking-[0.2em] text-ivory">
                  {tt.badge}
                </span>
              </button>
              <h3 className="mt-6 font-display text-2xl font-semibold text-ink sm:text-3xl">{l.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-stone-600 sm:text-base">{tt.description}</p>
            </motion.article>
          );
        })}
      </div>
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="mt-16 text-center font-display text-xl italic text-bordeaux-dark sm:text-2xl"
      >
        &ldquo;{t.lievitati.quote}&rdquo;
      </motion.p>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="lievitato-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setSelected(null)}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-night/90 p-5 backdrop-blur-md"
          >
            <motion.figure
              initial={{ scale: 0.85, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-2xl"
            >
              <img
                src={selected.image}
                alt={selected.name}
                className="max-h-[70vh] w-auto rounded-[2rem] border border-ivory/20 object-contain shadow-2xl"
              />
              <figcaption className="mt-5 text-center">
                <p className="font-display text-2xl font-semibold text-ivory sm:text-3xl">{selected.name}</p>
                <p className="mt-2 font-mono-alt text-[11px] uppercase tracking-[0.25em] text-gold">
                  {selected.badge}
                </p>
              </figcaption>
              <button
                data-testid="lievitato-lightbox-close"
                onClick={() => setSelected(null)}
                className="absolute -right-3 -top-3 flex h-11 w-11 items-center justify-center rounded-full bg-bordeaux text-ivory transition-transform hover:scale-110"
                aria-label="Chiudi foto"
              >
                <X className="h-5 w-5" />
              </button>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
